// Vuex Store
import store from "@/store";
import { KtznEventSubscriptionAction } from "@/framework/store/ktznEventSubscription/types";
import { ConversationAction, ConversationMutation } from "@/framework/store/conversation/types";
import { EventEmitter } from "@/framework/eventEmitter/eventEmitter";
// Message
import { Message } from "@/modules/staff/objects/conversation/Message";
import { QcConversation } from "@/modules/staff/objects/conversation/QcConversation";
import { ConversationService } from "@/framework/api/Staff/ChatService/ConversationService";
import { ErrorService } from "@/framework/errors/ErrorService";
import { userNameFilter } from "@/modules/staff/services/filter";

interface IConversationRepository {
  // pwaLoadChatMessages(
  //   isInitial: boolean,
  //   conversationId: string,
  //   limit: number,
  //   nextToken: string | null,
  //   fromInquiryHistory: boolean,
  //   isStaff: boolean
  // ): Promise<LoadChatMessagesResult>;

  checkMissingMessages(
    conversationId: string,
    nowLatestOrder: number,
    newOrder: number,
    targetConversationType?: string
  ): Promise<boolean>;

  getCurrentMessages(): Message[];

  getCurrentStaffMessages(): Message[];

  existsIn(createdMessage: Message, currentMessages: Message[]): boolean;

  sortMessages(messages: Message[]): Message[];

  getConversationName(conversation: QcConversation, currentUsername: string): string | null;

  isValidMessageLength(message: string, characterLimit?: number): boolean;
}

type VuexSubscription = () => void;

/**
 * Handles the conversation (chat) logic that is used in `fe-guest`, `fe-pwa` and `fe-staff`.
 */
export class ConversationRepository implements IConversationRepository {
  // MARK: - Vuex Subscription

  private unsubscribeVuexActions?: VuexSubscription;

  private readonly onNewMessage = new EventEmitter<Message>();
  private readonly onUpdatedMessage = new EventEmitter<Message>();
  private readonly onNewStaffMessage = new EventEmitter<Message>();
  private readonly onUpdatedStaffMessage = new EventEmitter<Message>();
  private readonly onUpdateConversationState = new EventEmitter<any>();
  private readonly onReceivedReadConversation = new EventEmitter<any>();
  private readonly onReceivedCreateQcUnreadConversation = new EventEmitter<any>();
  private readonly onReceivedCreateQcUnrepliedConversation = new EventEmitter<any>();
  private readonly onReceivedCreateUnReadMessage = new EventEmitter<any>();
  private readonly onConnectionDisconnected = new EventEmitter<any>();
  private readonly onConnectionEnded = new EventEmitter<any>();
  private readonly onNewConversationId = new EventEmitter<any>();
  private readonly onAllStaffAreBusy = new EventEmitter<any>();
  private readonly onTicketQueueConversationReloadNeeded = new EventEmitter<any>();
  private readonly onloadTickets = new EventEmitter<any>();
  private readonly onFetchLogs = new EventEmitter<any>();
  private readonly onCreateQcUnreadConversation = new EventEmitter<any>();
  private readonly onCreateQcUnrepliedConversation = new EventEmitter<any>();
  private readonly onUpdateAudit = new EventEmitter<any>();
  private readonly onUpdateMaxHandlingCap = new EventEmitter<any>();

  /**
   * Listens to incoming new messages
   */
  public get newMessage() {
    return this.onNewMessage.expose();
  }

  /**
   * Listens to incoming message updates
   */
  public get updatedMessage() {
    return this.onUpdatedMessage.expose();
  }

  /**
   * Listens to incoming new staff messages
   */
  public get newStaffMessage() {
    return this.onNewStaffMessage.expose();
  }

  /**
   * Listens to incoming staff message updates
   */
  public get updatedStaffMessage() {
    return this.onUpdatedStaffMessage.expose();
  }

  /**
   * Listens to conversation state updates
   */
  public get updateConversationState() {
    return this.onUpdateConversationState.expose();
  }

  /**
   * Listens to received read conversation
   */
  public get receivedReadConversation() {
    return this.onReceivedReadConversation.expose();
  }

  /**
   * Listens to received create unread conversation
   */
  public get receivedCreateQcUnreadConversation() {
    return this.onReceivedCreateQcUnreadConversation.expose();
  }

  /**
   * Listens to received create Unreplied conversation
   */
  public get receivedCreateQcUnrepliedConversation() {
    return this.onReceivedCreateQcUnrepliedConversation.expose();
  }

  /**
   * Listens to received create unread conversation
   */
  public get receivedCreateUnReadMessage() {
    return this.onReceivedCreateUnReadMessage.expose();
  }

  /**
   * Listens to received connection disconnected event
   */
  public get receivedConnectionDisconnected() {
    return this.onConnectionDisconnected.expose();
  }

  /**
   * Listens to received connection ended event
   */
  public get receivedConnectionEnded() {
    return this.onConnectionEnded.expose();
  }

  /**
   * Listens to received new conversation id event
   */
  public get receivedNewConversationId() {
    return this.onNewConversationId.expose();
  }

  /**
   * Listens to received event when we don't have any staff available including Japanese ones
   */
  public get receivedOnAllStaffAreBusy() {
    return this.onAllStaffAreBusy.expose();
  }

  /**
   * Listens to received event when we don't have any staff available including Japanese ones
   */
  public get receivedOnTicketQueueConversationReloadNeeded() {
    return this.onTicketQueueConversationReloadNeeded.expose();
  }

  /**
   * Listens to received event and reload tickets
   */
  public get receivedOnloadTickets() {
    return this.onloadTickets.expose();
  }

  /**
   * Listens to received event and reload logs
   */
  public get receivedOnFetchLogs() {
    return this.onFetchLogs.expose();
  }

  /**
   * Listens to conversation if there is any new message
   */
  public get receivedOnCreateQcUnreadConversation() {
    return this.onCreateQcUnreadConversation.expose();
  }

  /**
   * Listens to conversation if there is any message without any reply from staff
   */
  public get receivedOnCreateQcUnrepliedConversation() {
    return this.onCreateQcUnrepliedConversation.expose();
  }

  /**
   * Listens to received event for audit trail
   */
  public get receivedOnUpdateAudit() {
    return this.onUpdateAudit.expose();
  }

  /**
   * Listens to received event for max handling cap for staff and update tickets and profile pages
   */
  public get receivedOnUpdateMaxHandlingCap() {
    return this.onUpdateMaxHandlingCap.expose();
  }

  /**
   * Subscribes to conversation events
   */
  subscribeToConversationEvents = () => {
    this.unsubscribeVuexActions = store.subscribeAction({
      before: (action: any, state: any) => {
        // do something before calling the action
      },
      after: async (action: any, state: any) => {
        if (action.payload !== undefined) {
          switch (action.type) {
            //  ---------- Normal conversation ----------
            case ConversationAction.addMessageToCurrentMessages:
              this.onNewMessage.trigger(action.payload as Message); // Emit event
              break;
            case ConversationAction.updateMessageInCurrentMessages:
              this.onUpdatedMessage.trigger(action.payload as Message);
              break;
            // ---------- Staff conversation ----------
            case ConversationAction.addMessageToCurrentMessagesWithStaff:
              this.onNewStaffMessage.trigger(action.payload as Message);
              break;
            case ConversationAction.updateMessageInCurrentMessagesWithStaff:
              this.onUpdatedStaffMessage.trigger(action.payload as Message);
              break;
            // ---------- Conversation State ----------
            case ConversationAction.updateConversationState:
              this.onUpdateConversationState.trigger(action.payload);
              break;
            // ---------- Read / Unread Conversation ----------
            case ConversationAction.receivedReadConversation:
              this.onReceivedReadConversation.trigger(action.payload);
              break;
            case ConversationAction.receivedCreateQcUnreadConversation:
              this.onReceivedCreateQcUnreadConversation.trigger(action.payload);
              break;
            case ConversationAction.receivedCreateQcUnrepliedConversation:
              this.onReceivedCreateQcUnrepliedConversation.trigger(action.payload);
              break;
            case ConversationAction.receivedCreateUnReadMessage:
              this.onReceivedCreateUnReadMessage.trigger(action.payload);
              break;
          }
        } else {
          return;
        }
      },
    });
  };

  // TODO: Check if addable to subscribeToConversationEvents instead of mounted
  startSubscription = async () => {
    try {
      // disconnect from subscription
      // await store.dispatch(KtznEventSubscriptionAction.unsubscribeFromAllEvents);
      // (re) start subscription
      await store.dispatch(KtznEventSubscriptionAction.startWebsocketConnection);
    } catch (error) {
      console.warn("Error while disconnecting and connecting subscription : ", error);
      throw error;
    }
  };

  /**
   * Unsubscribes from conversation events
   */
  unsubscribeFromConversationEvents = () => {
    // unsubscribe from vuex store
    if (this.unsubscribeVuexActions) {
      this.unsubscribeVuexActions();
    } else {
      console.error("Error in unsubscribeFromConversationEvents: unsubscribeVuexActions callback undefined.");
    }
  };

  // MARK: - fe-pwa

  /**
   * Load the chat messages from a conversation
   * @param isInitial If is a newly created conversation
   * @param conversationId Conversation identifier
   * @param limit Maximal message limit for fetching
   * @param nextToken Next token from a previous fetch
   * @param fromInquiryHistory ???
   * @param isStaff If it's a staff conversation of normal conversation
   * @returns LoadChatMessagesResult
   */
  // Please use this in future
  /*pwaLoadChatMessages = async (
    isInitial: boolean,
    conversationId: string,
    limit: number,
    nextToken: string | null,
    fromInquiryHistory: boolean,
    isStaff: boolean
  ): Promise<LoadChatMessagesResult> => {
    const getConvoResult = await ConversationApi.getConversation(conversationId, limit, nextToken, isStaff);

    let chatMsg: Message[] = [];
    let members: ConvMember[] = [];
    if (getConvoResult !== null) {
      // Get the date in descending order, so sort in ascending order
      chatMsg = this.sortMessages(getConvoResult.messages.items);

      // WARN: is this really only needed for guest messages?
      if (!isStaff) {
        // Do not issue anything with the is_hidden flag set
        chatMsg = chatMsg.filter(m => {
          return !m.action.is_hidden;
        });
      }

      // Create members from convoResult
      if (getConvoResult.conversation_detail) {
        members = getConvoResult.members.items.filter((member: any) => {
          return member.join_status === JoinStatus.Joined;
        });
      }
    }

    let parsedMessages = await Parser.parseMessages(chatMsg);

    let conversationAction: string;
    if (isStaff && isInitial) {
      conversationAction = ConversationAction.setCurrentMessagesWithStaff;
    } else if (isStaff && !isInitial) {
      conversationAction = ConversationAction.addMessagesToCurrentMessagesWithStaff;
    } else if (!isStaff && isInitial) {
      conversationAction = ConversationAction.setCurrentMessages;
    } else {
      conversationAction = ConversationAction.addMessagesToCurrentMessages;
    }
    await store.dispatch(conversationAction, parsedMessages);

    return {
      conversation: getConvoResult,
      messages: store.getters.currentMessages,
      conversationMembers: members
    };
  };*/

  /**
   * Checks for missing messages and reorders?
   * @param conversationId Conversation identifier
   * @param nowLatestOrder
   * @param newOrder
   * @param targetConversationType (Optional)
   */
  checkMissingMessages = async (
    conversationId: string,
    nowLatestOrder: number,
    newOrder: number,
    targetConversationType?: string
  ): Promise<boolean> => {
    if (nowLatestOrder === 0) {
      // 一応ログに...
      console.log("latest order", nowLatestOrder, "new order", newOrder);
      return false;
    }
    if (conversationId !== store.getters.currentConversationId && store.getters.user.userType === "biz_staff") {
      return false;
    }
    if (nowLatestOrder + 1 < newOrder) {
      // If the new message is 2 or more larger than the order of the existing message, it means that the message is being sent.
      let orders = [];
      for (let i = nowLatestOrder + 1; i < newOrder; i++) {
        orders.push(i);
      }
      const res = await new ConversationService().fetchMissingMessages(conversationId, orders);
      if (res.isErr()) {
        await new ErrorService().handleError(res.error);
        return false;
      }

      const missingMessages = res.value;

      if (targetConversationType && targetConversationType == "with_staff") {
        await store.dispatch(ConversationAction.addMessagesToCurrentMessagesWithStaff, missingMessages);
      } else {
        await store.dispatch(ConversationAction.addMessagesToCurrentMessages, missingMessages);
      }
      return true;
    } else {
      return false;
    }
  };

  /**
   * Returns the sorted current messages from the store
   * @returns List of conversation messages
   */
  getCurrentMessages = (): Message[] => {
    let messages: Message[] = [];
    if (store && store.getters && store.getters.currentMessages) {
      messages = store.getters.currentMessages as Message[];
    }
    return this.sortMessages(messages);
  };

  /**
   * Returns the current staff conversation messages from the store
   * @returns List of staff conversation messages
   */
  getCurrentStaffMessages = (): Message[] => {
    let messages: Message[] = [];
    if (store && store.getters && store.getters.currentMessages) {
      messages = store.getters.currentMessagesWithStaff as Message[];
    }
    return this.sortMessages(messages);
  };

  /**
   * Checks if a given message exists in a given array of messages.
   * @param createdMessage
   * @param currentMessages List of current messages
   * @returns Boolean
   */
  existsIn = (createdMessage: Message, currentMessages: Message[]): boolean => {
    return currentMessages.filter((m) => m.id === createdMessage.id).length > 0;
  };

  /**
   * Sorts a give array of `Message` objects according to their `order` property in **ascending** order)
   */
  sortMessages = (messages: Message[]): Message[] => {
    return messages.sort((a, b) => (a.order < b.order ? -1 : 1));
  };

  /**
   * Gets a somehow??? filtered conversation name from a conversation. Uses `userNameFilter` from helpers.
   * @param conversation Conversation object
   * @param currentUsername
   * @returns conversation name or null
   */
  getConversationName = (conversation: QcConversation, currentUsername: string): string | null => {
    if (conversation.conversation_type === "p2p") {
      const other = conversation.members.items.find((member) => member.user.access_id != currentUsername);
      if (other) {
        let res = userNameFilter(other.user.name);
        if (res) {
          return `${res}`;
        }
        return null;
      } else {
        return null;
      }
    }
    return conversation.name;
  };

  /**
   * Saves the current conversationId to the vuex store.
   * Apparently needed for the conversation / chat logic in the app???
   * @param conversationId Conversation Identifier
   */
  saveCurrentConversationIdToStore = async (conversationId: string): Promise<void> => {
    store.commit(ConversationMutation.SET_CURRENT_CONVERSATION_ID, conversationId);
    await store.dispatch(KtznEventSubscriptionAction.subscribeToConversation, conversationId);
  };

  /**
   * Removes the conversation id from the vuex store.
   * Apparently needed for the conversation / chat logic in the app???
   */
  removeCurrentConversationIdFromStore = async (id?: string) => {
    store.commit(ConversationMutation.SET_CURRENT_CONVERSATION_ID, "");
    if (id) {
      await store.dispatch(KtznEventSubscriptionAction.unsubscribeFromConversation, id);
    }
  };

  /**
   * Validates (somehow???) if a given message exceeds the maximal length.
   * @param message
   * @param characterLimit (Optional) Maximal characters per message (Default = 400)
   * @returns If message is valid length
   */
  isValidMessageLength = (message: string, characterLimit: number = 400): boolean => {
    if (message.includes('{"messages":')) {
      try {
        const jsonContent = JSON.parse(message);
        const contentArray = jsonContent.messages;
        // @ts-ignore
        contentArray.forEach((content) => {
          if (content.type === "text" && content.text.content.length > characterLimit) {
            return false;
          }
        });
      } catch {
        return false;
      }
    } else if (message.length > characterLimit) {
      return false;
    }
    return true;
  };
}
